import {EventBus} from '../../plugins/eventBus';
import {BET_STATUS, notifyReviewProposal, notifyReviewOutcome} from '@/services/parentBridge/betPlacedEvent';

const state = {
    io: {},
    socket_enabled: false,
    enabledSocketSettlementOddLive: false,
    shiftOutcomeAlreadyDefined: false
};

const getters = {
    getSocketEnabled: state => state.socket_enabled,
    getEnabledSocketSettlementOddLive: state => state.enabledSocketSettlementOddLive,
    getShiftOutcomeAlreadyDefined: state => state.shiftOutcomeAlreadyDefined
};

const actions = {
    // ------------------------------------------------------------
    // CONNECTION MANAGEMENT
    // ------------------------------------------------------------
    socket_login: (context, data) => {
        const params = {room: data.platform_id};
        if (context.rootState.socket.io && context.rootState.socket.io.emit) {
            context.rootState.socket.io.emit('join', params, () => {
                context.commit('SET_SOCKET_ENABLED', true);
            });
        }
    },

    socket_login_generic: (context, room) => {
        const params = {room: room};
        if (context.rootState.socket.io && context.rootState.socket.io.emit) {
            context.rootState.socket.io.emit('join', params, () => {
                context.commit('SET_SOCKET_ENABLED', true);
            });
        }
    },

    socket_close: (context) => {
        context.commit('SET_SOCKET_ENABLED', false);
    },

    socket_enable: (context) => {
        context.commit('SET_SOCKET_ENABLED', true);
    },

    // ------------------------------------------------------------
    // LIVE DATA (High Frequency - Direct Commits)
    // ------------------------------------------------------------

    // 1. UPDATE ODDS (Verde/Rosso) -> Aggiorna Lista, Dettaglio E Widget Scroller
    SOCKET_ODDS_CHANGE_LIVE: (context, data) => {
        if (!context.state.socket_enabled) return;

        // Commit a previewLive (Lista)
        context.commit('previewLive/UPDATE_ODDS_FROM_SOCKET', data, {root: true});

        // Commit a detailLive (Dettaglio Evento)
        context.commit('detailLive/UPDATE_ODDS_FROM_SOCKET', data, {root: true});

        // Nuova riga per aggiornare il widget laterale!
        context.dispatch('scrollerWidgetEventLive/socketUpdateOdds', data, {root: true});

        // Aggiorna mixedPreview (topsports)
        context.commit('topsports/UPDATE_ODDS_FROM_SOCKET', data, {root: true});

        // Aggiorna featured events (widget scrollabile in cima)
        context.dispatch('featuredEvents/socketUpdateOdds', data, {root: true});

        // Riapertura quota: se nel carrello c'è una quota sospesa che torna status=1,
        // togli il flag così l'utente può rimpiazzare senza altre azioni.
        context.dispatch('cart/socketClearSuspendedOnRecover', data, {root: true});
    },

    // 2. UPDATE LIVESCORE (Tempo, Score, Status) -> Aggiorna Lista, Dettaglio E Widget Scroller
    SOCKET_LIVESCORE_LIVE: (context, data) => {
        if (!context.state.socket_enabled) return;

        // Parsing dati puliti: gestione formato ["LIVESCORE_LIVE", [data]]
        let cleanData = data;

        if (Array.isArray(data) && data.length === 2 && typeof data[0] === 'string' && Array.isArray(data[1])) {
            cleanData = data[1];
        }

        // Commit a previewLive (Lista)
        context.commit('previewLive/UPDATE_LIVESCORE_FROM_SOCKET', cleanData, {root: true});

        // Commit a detailLive (Dettaglio Evento)
        context.commit('detailLive/UPDATE_LIVESCORE_FROM_SOCKET', cleanData, {root: true});

        // Nuova riga per aggiornare il widget laterale!
        context.commit('scrollerWidgetEventLive/UPDATE_LIVESCORE_FROM_SOCKET', cleanData, {root: true});

        // Aggiorna mixedPreview (topsports)
        context.commit('topsports/UPDATE_LIVESCORE_FROM_SOCKET', cleanData, {root: true});

        // Aggiorna featured events (widget scrollabile in cima)
        context.commit('featuredEvents/UPDATE_LIVESCORE_FROM_SOCKET', cleanData, {root: true});
    },

    // 3. NEW LIVE EVENT (Instant Add) -> Solo Lista (solitamente)
    SOCKET_NEW_EVENT_LIVE: (context, data) => {
        if (context.state.socket_enabled) {
            const eventData = Array.isArray(data) ? data[0] : data;

            // Aggiungi alla LISTA istantaneamente
            context.commit('previewLive/ADD_NEW_EVENT_LIVE', eventData, {root: true});
        }
    },

    // ------------------------------------------------------------
    // SETTLEMENT & LOGIC
    // ------------------------------------------------------------

    SOCKET_SETTLEMENT_ODD_LIVE: (context, data) => {
        if (context.state.socket_enabled && context.state.enabledSocketSettlementOddLive) {
            // Logica legacy per disabilitare quote sospese
            context.dispatch('odds/disableAndOrderOddsDefined', data, {root: true});
        }
    },

    SOCKET_DEFINE_BET_LIST: (context, data) => {
        if (context.state.socket_enabled) {
            context.dispatch('odds/settlementOddsLive', data, {root: true});
        }
    },

    // ------------------------------------------------------------
    // BETTING & CART MANAGEMENT
    // ------------------------------------------------------------

    SOCKET_PLACE_BET_CHANGED: async (context, payload) => {
        if (!context.state.socket_enabled) return;
        try {
            const {parentBet, status, betCode, amount_real, _multiple_detail, _integral_detail, _system_detail, code} = payload;

            if (status === 'revived') {
                // Parent bridge: the operator proposes a different bet for the one in review
                notifyReviewProposal(payload);

                // Capture snapshot before removing so the change panel can display full odds
                const snapshot = (context.rootState.cart.accepting_bets || []).find(b => String(b.code) === String(parentBet));

                await context.dispatch('cart/removeBetFromAcceptanceByCode', parentBet, {root: true});

                let winning_total;
                if (_multiple_detail?.winning_total != null) winning_total = _multiple_detail.winning_total;
                else if (_integral_detail?.winning_total_max != null) winning_total = _integral_detail.winning_total_max;
                else if (_system_detail?.winning_all_total != null) winning_total = _system_detail.winning_all_total;

                // Merge admin's revised odds onto the snapshot: keep the full rendering data
                // from the snapshot (event_description, market, competitors, sport, ...) and
                // overlay outcome_odd_value (admin proposal) + outcome_odd_value_for_order
                // (player's original request) from the socket payload, so the cart can show
                // old→new like the classic placeBet flow.
                const reviewOdds = Array.isArray(payload.odds) ? payload.odds : [];
                const snapshotOdds = snapshot?.odds || [];
                const mergedOdds = snapshotOdds.map(snapOdd => {
                    const rev = reviewOdds.find(o =>
                        (o.unique_id != null && (o.unique_id == snapOdd.unique_id || o.unique_id == snapOdd.outcome_id)) ||
                        (o.outcome_id != null && (o.outcome_id == snapOdd.unique_id || o.outcome_id == snapOdd.outcome_id))
                    );
                    if (!rev) return snapOdd;
                    return {
                        ...snapOdd,
                        outcome_odd_value: rev.outcome_odd_value != null ? rev.outcome_odd_value : snapOdd.outcome_odd_value,
                        outcome_odd_value_for_order: rev.outcome_odd_value_for_order != null
                            ? rev.outcome_odd_value_for_order
                            : (snapOdd.outcome_odd_value_for_order != null ? snapOdd.outcome_odd_value_for_order : snapOdd.outcome_odd_value),
                    };
                });

                const betInfo = {code, betCode, parentBet, amount_real, winning_total};
                const enrichedBetInfo = snapshot
                    ? { ...snapshot, ...betInfo, odds: mergedOdds }
                    : betInfo;
                await context.dispatch('cart/addAcceptBetWithChanges', enrichedBetInfo, {root: true});

                EventBus.$emit('closeModalReserveAlert');
                EventBus.$emit('cleanTheCart');
            }
            await context.dispatch('cart/placeBetChanged', payload, {root: true});
        } catch (e) {
            console.error("Socket Bet Error:", e);
        }
    },

    SOCKET_RESERVE_BET_REJECT: async (context, payload) => {
        if (!context.state.socket_enabled) return;
        try {
            // Parent bridge: refused by the operator, or the proposal refused by the player
            notifyReviewOutcome(BET_STATUS.REJECTED, payload.code);
            await Promise.all([
                context.dispatch('cart/removeBetFromAcceptance', payload, {root: true}),
                // payload.code è il codice prenotazione (parentBet nelle entry accepted_with_change)
                context.dispatch('cart/removeBetFromAcceptedWithChangeByParent', payload.code, {root: true}),
                context.dispatch('cart/addRejectedBet', payload, {root: true})
            ]);
            EventBus.$emit('closeModalReserveAlert');
            EventBus.$emit('cleanTheCart');
        } catch (e) {
            console.error("Socket Reject Error:", e);
        }
    },

    SOCKET_RESERVE_BET_ACCEPT: async (context, payload) => {
        if (!context.state.socket_enabled) return;
        try {
            // Parent bridge: the bet in review accepted by the operator, or the proposal accepted by the player
            notifyReviewOutcome(BET_STATUS.ACCEPTED, payload.code);
            // Merge the stored snapshot (odds, amounts) so BetConfirmedMarketView can display full details
            const snapshot = (context.rootState.cart.accepting_bets || []).find(b => String(b.code) === String(payload.code));
            const enrichedPayload = snapshot ? { ...snapshot, ...payload } : payload;
            await Promise.all([
                context.dispatch('cart/removeBetFromAcceptance', payload, {root: true}),
                context.dispatch('cart/addAcceptedBets', enrichedPayload, {root: true})
            ]);
            // Show BetConfirmedMarketView only if not already set.
            // If the user already accepted a change proposal, confirmed_bet is set with full odds —
            // don't overwrite it with the socket payload which may lack the odds snapshot.
            // Senza snapshot (quote/importi) la conferma uscirebbe vuota: non mostrarla
            const hasSnapshot = Array.isArray(enrichedPayload.odds) && enrichedPayload.odds.length > 0;
            if (hasSnapshot && !context.rootState.cart.confirmed_bet) {
                await context.dispatch('cart/setConfirmedBet', {
                    bet_code:         enrichedPayload.code,
                    is_booked:        false,
                    type:             enrichedPayload.type            || 'single',
                    amount:           enrichedPayload.amount          || 0,
                    odds_total:       enrichedPayload.odds_total      || 0,
                    winning:          enrichedPayload.winning         || 0,
                    winning_total:    enrichedPayload.winning_total   || 0,
                    bonus:            enrichedPayload.bonus           || 0,
                    bonus_percentage: enrichedPayload.bonus_percentage|| 0,
                    odds:             enrichedPayload.odds            || [],
                }, {root: true});
            }
            EventBus.$emit('closeModalReserveAlert');
            EventBus.$emit('cleanTheCart');
        } catch (e) {
            console.error("Socket Accept Error:", e);
        }
    },

    // ------------------------------------------------------------
    // LOCAL STATE MUTATIONS
    // ------------------------------------------------------------
    SET_SOCKET: (context, payload) => {
        context.commit('setSocket', payload);
    },
    setEnabledSocketSettlementOddLive: (context, payload) => {
        context.commit('SET_ENABLED_SOCKET_SETTLEMENT_ODD_LIVE', payload);
    },
    setShiftOutcomeAlreadyDefined: (context, payload) => {
        context.commit('SET_SHIFT_OUTCOME_ALREADY_DEFINED', payload);
    },
};

const mutations = {
    setSocket: (state, socket) => {
        state.io = socket;
    },
    SET_SOCKET_ENABLED(state, payload) {
        state.socket_enabled = payload;
    },
    SET_ENABLED_SOCKET_SETTLEMENT_ODD_LIVE(state, payload) {
        state.enabledSocketSettlementOddLive = payload;
    },
    SET_SHIFT_OUTCOME_ALREADY_DEFINED(state, payload) {
        state.shiftOutcomeAlreadyDefined = payload;
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
    modules: {}
};